/**
 * Images feature — entry point
 *
 * Structure:
 *   hooks/useImages.js          — list, toggle-active, delete
 *   hooks/useImageUpload.js     — file queue, progress, upload
 *   components/ImageCard.jsx    — single card in the grid
 *   components/ImageUploadModal.jsx  — drag-drop upload modal
 *   components/ImagePreviewModal.jsx — full-size preview modal
 */
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import TypeGuide from '../../components/TypeGuide.jsx'
import { RiImageAddLine, RiInformationLine, RiSearchLine } from 'react-icons/ri'
import {
  Btn, Select, PageHeader, EmptyState,
  Pagination, FilterBar,
} from '../../components/ui/design.jsx'

import { useImages } from './hooks/useImages.js'
import { useImageUpload } from './hooks/useImageUpload.js'
import ImageCard from './components/ImageCard.jsx'
import ImageUploadModal from './components/ImageUploadModal.jsx'
import ImagePreviewModal from './components/ImagePreviewModal.jsx'

export default function ImagesPage() {
  const location = useLocation()
  const initialTypeId = location.state?.typeId ?? ''

  const [searchVal, setSearchVal] = useState('')
  const [filterActive, setFilterActive] = useState('')
  const [showGuide, setShowGuide] = useState(false)
  const [previewing, setPreviewing] = useState(null)

  const imageHook = useImages(initialTypeId)
  const {
    types, typeId, setTypeId, images, loading, pagination,
    currentType, atCapacity, load, toggleActive, remove,
  } = imageHook

  const uploadHook = useImageUpload({
    onSuccess: () => {
      load(1, { search: searchVal, filterActive })
    },
  })

  const handleSearch = (e) => {
    if (e.key === 'Enter') load(1, { search: searchVal, filterActive })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gambar"
        description="Unggah gambar lalu aktifkan menggunakan tombol geser agar tampil di website."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => setShowGuide((v) => !v)}>
              <RiInformationLine /> {showGuide ? 'Sembunyikan Panduan' : 'Panduan'}
            </Btn>
            <Btn onClick={uploadHook.open}>
              <RiImageAddLine /> Unggah
            </Btn>
          </div>
        }
      />

      <FilterBar>
        <Select value={typeId} onChange={(e) => setTypeId(e.target.value)} className="w-44">
          <option value="">Semua tipe</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <div className="relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
          <input
            placeholder="Cari judul atau nama file"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            onKeyDown={handleSearch}
            className="h-9 w-52 rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <Select
          value={filterActive}
          onChange={(e) => { setFilterActive(e.target.value); load(1, { search: searchVal, filterActive: e.target.value }) }}
          className="w-36"
        >
          <option value="">Semua status</option>
          <option value="true">Aktif saja</option>
          <option value="false">Nonaktif saja</option>
        </Select>
      </FilterBar>

      {showGuide && <TypeGuide typeSlug={currentType?.slug} />}

      {currentType && !currentType.is_unlimited && (
        <div className={`rounded-xl px-4 py-3 text-sm border ${
          atCapacity
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-blue-50 text-blue-700 border-blue-200'
        }`}>
          <strong>{currentType.name}</strong>: {currentType.active_images_count} dari {currentType.max_active} slot aktif terisi.
          {atCapacity && ' Nonaktifkan salah satu gambar sebelum mengaktifkan yang lain.'}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {loading && <EmptyState message="Memuat…" />}
        {!loading && images.length === 0 && <EmptyState message="Belum ada gambar." />}
        {images.map((img) => (
          <ImageCard
            key={img.id}
            img={img}
            atCapacity={atCapacity}
            onPreview={setPreviewing}
            onToggle={(img) => toggleActive(img, pagination?.current_page)}
            onDelete={(img) => remove(img, pagination?.current_page)}
          />
        ))}
      </div>

      <Pagination pagination={pagination} onPage={(p) => load(p, { search: searchVal, filterActive })} />

      <ImageUploadModal
        open={uploadHook.uploading}
        onClose={uploadHook.close}
        types={types}
        uploadHook={uploadHook}
      />

      <ImagePreviewModal
        image={previewing}
        onClose={() => setPreviewing(null)}
      />
    </div>
  )
}
